import InfoCircleOutlined from "@ant-design/icons/lib/icons/InfoCircleOutlined";
import {
  convertDurationToDurationOption,
  printUserFriendlyDuration,
  sortApprovalOptions,
  timeUnitLabelOptions,
} from "@p0security/shared/permission-requests/util";
import {
  ApprovalRule,
  type DurationOption,
  RequestorProfile,
  UserApprovalOptions,
  isDurationLimited,
} from "@p0security/shared/types/policy/types";
import {
  Button,
  Checkbox,
  CheckboxChangeEvent,
  CheckboxOptionType,
  Flex,
  InputNumber,
  Popover,
  Select,
  Space,
  Typography,
} from "@p0security/ui";
import {
  resolveTenantMaxDurations,
  useDurationSettings,
} from "components/Admin/useDurationSettings";
import { newApprovalRuleOfType } from "components/Jit/Policy/store/mutations";
import { defaultMaxFor } from "components/Jit/Policy/utils";
import {
  approvalTypeLabels,
  getApprovalIcon,
} from "components/Jit/Policy/views/common";
import { PillSelect, PillSelectOption } from "components/common/PillSelect";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { CheckboxValueType } from "types/antd";

import { PolicyEditorContext } from "../../store/RoutingRuleEditorContext.tsx";
import { EscalationApproval } from "../EscalationApproval.tsx";
import { IdpGroupInput } from "../IdpGroupInput.tsx";
import { OnCallAllow } from "../OnCallAllow.tsx";
import { RequestorProfileInput } from "../RequestorProfileInput.tsx";
import { LocalAccessRuleContext } from "./AccessEditor.tsx";

type Props = {
  cancelButton?: React.ReactNode;
  onSave: (rule: ApprovalRule) => void;
};
const baseSelectionTypes: ApprovalRule["type"][] = [
  "group",
  "requestor-profile",
  "p0",
  "deny",
  "persistent",
];

const autoApprovalTypes: ApprovalRule["type"][] = ["auto", "escalation"];

export const AccessRuleEditor: React.FC<Props> = ({ cancelButton, onSave }) => {
  const { localRule, setLocalRule, isDirty } = useContext(
    LocalAccessRuleContext
  );

  const { installedApprovers } = useContext(PolicyEditorContext);

  const handleSelectAccessType = useCallback(
    (selection: ApprovalRule["type"]) => {
      setLocalRule(newApprovalRuleOfType(selection, installedApprovers));
    },
    [installedApprovers, setLocalRule]
  );

  const handleRequestorProfileChange = useCallback(
    (requestorProfile: RequestorProfile) => {
      if (localRule?.type !== "requestor-profile") {
        return;
      }

      const newRule =
        requestorProfile.directory === "okta"
          ? // Only showing property override for Okta right now, as Workspace has a more opinionated way of accessing the user's manager.
            { ...localRule, ...requestorProfile }
          : {
              type: "requestor-profile" as const,
              directory: requestorProfile.directory,
            };
      setLocalRule(newRule);
    },
    [setLocalRule, localRule]
  );

  const policyOptions = useMemo(() => {
    if (!localRule) {
      return [];
    }

    if (localRule.type === "deny") {
      return [];
    }

    const options: CheckboxOptionType[] = [];
    options.push({
      value: "requireReason",
      label: "Require reason with request",
    });
    options.push({
      value: "requireDuration",
      label: "Require duration with request",
    });
    options.push({
      value: "requirePreapproval",
      label: "Require preapproval with request",
    });

    if (
      localRule.type === "p0" ||
      localRule.type === "group" ||
      localRule.type === "escalation" ||
      localRule.type === "requestor-profile"
    ) {
      options.push({
        value: "allowOneParty",
        label: "Allow self-approvals",
      });
      options.push({
        value: "breakGlassApprover",
        label: "Approve break-glass access",
      });
    }
    return options;
  }, [localRule]);

  const handleChangeApprovalOptions = useCallback(
    (options: CheckboxValueType[]) => {
      if (!localRule) {
        return;
      }

      if (localRule.type === "deny") {
        return;
      }

      setLocalRule({
        ...localRule,
        options: convertApprovalOptionsArrayToObject(
          options as ApprovalOptionsArray
        ),
      });
    },
    [localRule, setLocalRule]
  );

  const durationSettings = useDurationSettings();

  // Undefined hides the checkbox for an action that can't have a max
  // duration (Deny)
  const actionMaxDuration = useMemo(() => {
    if (!localRule || !isDurationLimited(localRule)) {
      return undefined;
    }
    return defaultMaxFor(
      localRule.type,
      resolveTenantMaxDurations(durationSettings.doc?.data)
    );
  }, [localRule, durationSettings.doc?.data]);

  const handleToggleMaxDuration = useCallback(
    (event: CheckboxChangeEvent) => {
      if (!localRule || !isDurationLimited(localRule)) {
        return;
      }
      if (!event.target.checked) {
        // Remove the key instead of setting it to undefined: isDirty compares
        // with isEqual, which treats a missing key and `undefined` as different
        const { maxDuration, ...withoutMaxDuration } = localRule;
        setLocalRule(withoutMaxDuration);
        return;
      }
      if (actionMaxDuration) {
        setLocalRule({ ...localRule, maxDuration: { ...actionMaxDuration } });
      }
    },
    [actionMaxDuration, localRule, setLocalRule]
  );

  const maxDuration =
    localRule && isDurationLimited(localRule)
      ? localRule.maxDuration
      : undefined;

  // Over the limit: shown in red, and the action can't be submitted
  const exceedsMaxDuration =
    !!maxDuration &&
    !!actionMaxDuration &&
    sortApprovalOptions(maxDuration, actionMaxDuration) > 0;

  // The typed number, kept separately so the field can be empty while someone
  // retypes it. maxDuration only changes once it holds a number
  const [maxDurationTime, setMaxDurationTime] = useState(
    maxDuration?.time ?? null
  );
  // Follow maxDuration when it changes elsewhere, e.g. checking the box
  useEffect(() => {
    setMaxDurationTime(maxDuration?.time ?? null);
  }, [maxDuration?.time]);

  const updateMaxDuration = useCallback(
    (change: Partial<Pick<DurationOption, "time" | "unit">>) => {
      if (
        !localRule ||
        !isDurationLimited(localRule) ||
        !localRule.maxDuration
      ) {
        return;
      }
      setLocalRule({
        ...localRule,
        // Also rebuilds the "3 hours" display text from the new time and unit
        maxDuration: convertDurationToDurationOption({
          ...localRule.maxDuration,
          ...change,
        }),
      });
    },
    [localRule, setLocalRule]
  );

  const handleChangeMaxDurationTime = useCallback(
    (time: number | null) => {
      setMaxDurationTime(time);
      if (time) {
        updateMaxDuration({ time });
      }
    },
    [updateMaxDuration]
  );

  const handleChangeMaxDurationUnit = useCallback(
    (unit: DurationOption["unit"]) => updateMaxDuration({ unit }),
    [updateMaxDuration]
  );

  const saveRule = useCallback(
    () => localRule && onSave(localRule),
    [localRule, onSave]
  );

  const available = useMemo<PillSelectOption<ApprovalRule["type"]>[]>(() => {
    const keys: ApprovalRule["type"][] = [
      ...baseSelectionTypes,
      ...(installedApprovers.length ? autoApprovalTypes : []),
    ];
    return keys.map((sel) => ({
      icon: getApprovalIcon(sel),
      label: approvalTypeLabels[sel] ?? sel,
      value: sel,
    }));
  }, [installedApprovers]);

  return (
    <Space direction="vertical" className="w-full">
      <Flex align="center" gap={12} justify="space-between" wrap="wrap">
        <PillSelect<ApprovalRule["type"]>
          value={localRule?.type}
          onChange={handleSelectAccessType}
          options={available}
          placeholder="Choose an action"
        />
        {cancelButton}
      </Flex>
      {localRule?.type === "group" && (
        <IdpGroupInput value={localRule} onChange={setLocalRule} />
      )}
      {localRule?.type === "auto" && installedApprovers && (
        <OnCallAllow
          value={localRule}
          installed={installedApprovers}
          onChange={setLocalRule}
        />
      )}
      {localRule?.type === "escalation" && installedApprovers && (
        <EscalationApproval
          value={localRule}
          installed={installedApprovers}
          onChange={setLocalRule}
        />
      )}
      {localRule?.type === "requestor-profile" && (
        <RequestorProfileInput
          value={localRule}
          onChange={handleRequestorProfileChange}
        />
      )}
      {localRule && policyOptions.length > 0 && localRule.type !== "deny" && (
        <div className="flex flex-col">
          <Checkbox.Group
            value={convertApprovalOptionsObjectToArray(localRule.options)}
            onChange={handleChangeApprovalOptions}
          >
            <div className="flex flex-col">
              {policyOptions.map((option) => (
                <Checkbox key={option.value} value={option.value}>
                  {option.label}{" "}
                  {option.value === "breakGlassApprover" && (
                    <Popover
                      content={
                        <div style={{ maxWidth: 350 }}>
                          Only available for SSH access
                        </div>
                      }
                    >
                      <InfoCircleOutlined className="info" />
                    </Popover>
                  )}
                </Checkbox>
              ))}
            </div>
          </Checkbox.Group>
          {/* Outside the group: the group saves each checked value as
              `options.<value>: true`, but this one sets `maxDuration` */}
          {actionMaxDuration && isDurationLimited(localRule) && (
            <>
              <Checkbox
                checked={!!localRule.maxDuration}
                disabled={durationSettings.loading}
                onChange={handleToggleMaxDuration}
              >
                Match policy action by requested duration{" "}
                <Popover
                  content={
                    <div className="max-w-87.5">
                      <p>
                        Set a custom maximum duration for this action so it only
                        applies to access requests with a duration at or below
                        this maximum.
                      </p>
                      <p className="mb-0">
                        Longer requests go to another action that allows them.
                        If none does, P0 shortens the duration to the longest
                        any matching action allows and grants the request.
                      </p>
                    </div>
                  }
                >
                  <InfoCircleOutlined className="info" />
                </Popover>
              </Checkbox>
              {localRule.maxDuration && (
                <>
                  <Flex align="center" gap={8} className="ml-6!">
                    Maximum duration:
                    <Space.Compact>
                      <InputNumber
                        aria-label="Maximum duration"
                        status={exceedsMaxDuration ? "error" : undefined}
                        disabled={durationSettings.loading}
                        min={1}
                        precision={0}
                        value={maxDurationTime}
                        onChange={handleChangeMaxDurationTime}
                      />
                      <Select
                        aria-label="Maximum duration unit"
                        className="w-24"
                        disabled={durationSettings.loading}
                        options={timeUnitLabelOptions}
                        value={localRule.maxDuration.unit}
                        onChange={handleChangeMaxDurationUnit}
                      />
                    </Space.Compact>
                  </Flex>
                  <Typography.Text
                    type={exceedsMaxDuration ? "danger" : "secondary"}
                    className="ml-6"
                  >
                    Up to the organization maximum of{" "}
                    {printUserFriendlyDuration(actionMaxDuration)}
                  </Typography.Text>
                </>
              )}
            </>
          )}
        </div>
      )}
      {localRule && (
        <Button
          type="primary"
          onClick={saveRule}
          disabled={
            !isDirty ||
            exceedsMaxDuration ||
            (!!maxDuration && !maxDurationTime)
          }
        >
          Submit
        </Button>
      )}
    </Space>
  );
};

const convertApprovalOptionsObjectToArray = (
  approvalOptions: UserApprovalOptions | undefined
) => {
  const options = [];
  if (approvalOptions?.allowOneParty) {
    options.push("allowOneParty");
  }
  if (approvalOptions?.requireReason) {
    options.push("requireReason");
  }
  if (approvalOptions?.requireDuration) {
    options.push("requireDuration");
  }
  if (approvalOptions?.requirePreapproval) {
    options.push("requirePreapproval");
  }
  if (approvalOptions?.breakGlassApprover) {
    options.push("breakGlassApprover");
  }
  return options;
};

type ApprovalOptionsArray = (
  | "allowOneParty"
  | "breakGlassApprover"
  | "requireDuration"
  | "requirePreapproval"
  | "requireReason"
)[];

const convertApprovalOptionsArrayToObject = (options: ApprovalOptionsArray) => {
  return Object.fromEntries(options.map((o) => [o, true]));
};
